import z from "zod";

import { ErrorClassification } from "../../../sentry/classification";
import type { ErrorDetails } from "../domain";

const personErrorTypeValues = [
  "UNKNOWN",
  "PERSON_NOT_FOUND",
  "USER_NOT_FOUND",
  "PERSON_NOT_FOUND_FOR_EMAIL_INVITE",
  "PERSON_INVITE_CONFLICT",
  "PERSON_ID_REQUIRED",
  "PERSON_NOT_FOUND_BY_PHONE",
  "PERSON_MISSING_PRIMARY_PHONE",
  "PERSON_PROFILE_PHOTO_NOT_FOUND",
  "INVITATION_ALREADY_RESCINDED",
  "INVITATION_ALREADY_ACCEPTED",
  "PERSON_MISSING_EMAIL_ADDRESS",
  "INVITATION_INACTIVE",
  "LINK_DESTINATION_MISSING",
  "LINK_PROJECT_ID_REQUIRED",
  "LINK_PROJECT_ID_INVALID",
  "LINK_AUTH_REQUIRED",
  "LINK_NOT_FOUND",
  "LINK_TYPE_NOT_SUPPORTED",
  "LINK_ACCESS_FAILED",
  "INVALID_API_KEY",
  "ORGANIZATION_NOT_FOUND",
  "UNAUTHORIZED",
  "PERSON_GROUP_NOT_FOUND",
  "PERSON_GROUP_KEY_ALREADY_EXISTS",
  "PERSON_GROUP_KEY_NOT_FOUND",
  "PERSON_GROUP_ARCHIVE_BLOCKED",
  "ACCOUNT_MANAGER_PROMOTE_BLOCKED",
  "ACCOUNT_MANAGEMENT_GROUP_REQUIRES_PROMOTE_FLOW",
  "COVERING_PERSON_INELIGIBLE",
  "UNKNOWN_CONTACT_NOT_FOUND",
  "UNKNOWN_CONTACT_CLASSIFICATION_FAILED",
  "UNKNOWN_CONTACT_DRAIN_RUN_ID_INVALID",
  "DUPLICATE_CLAIMANT_MERGE_RUN_ID_INVALID",
  "UNKNOWN_CONTACT_MANIFEST_RUN_ID_MISSING",
  "UNKNOWN_CONTACT_CHUNKED_MERGE_IN_TRANSACTION",
  "UNKNOWN_CONTACT_CHUNK_BUDGET_EXCEEDED",
  "UNKNOWN_CONTACT_RETENTION_RUN_ID_INVALID",
  "UNKNOWN_CONTACT_RETENTION_TIER_INVALID",
  "NAV_KEY_INVALID",
  "SIDEBAR_ROW_NOT_REORDERABLE",
] as const;
const personErrorTypeSchema = z.enum(personErrorTypeValues);
export const PersonErrorType = personErrorTypeSchema.enum;
type PersonErrorType = z.infer<typeof personErrorTypeSchema>;

export type PersonErrors =
  | { errorType: typeof PersonErrorType.UNKNOWN; data: undefined }
  | {
      errorType: typeof PersonErrorType.PERSON_NOT_FOUND;
      data: { personId?: string; userId?: string; projectIdOfHomeowner?: number };
    }
  | { errorType: typeof PersonErrorType.USER_NOT_FOUND; data: { userId?: string; personId?: string } }
  | { errorType: typeof PersonErrorType.PERSON_NOT_FOUND_FOR_EMAIL_INVITE; data: { email: string } }
  | {
      errorType: typeof PersonErrorType.PERSON_INVITE_CONFLICT;
      data: { email?: string; phoneNumber?: string; reason: string; errorType?: string };
    }
  | { errorType: typeof PersonErrorType.PERSON_ID_REQUIRED; data: undefined }
  | { errorType: typeof PersonErrorType.PERSON_NOT_FOUND_BY_PHONE; data: { phoneNumber: string } }
  | {
      errorType: typeof PersonErrorType.PERSON_MISSING_PRIMARY_PHONE;
      data: { personId?: string; firstName?: string; lastName?: string };
    }
  | {
      errorType: typeof PersonErrorType.PERSON_PROFILE_PHOTO_NOT_FOUND;
      data: { personId?: string; profilePhotoId?: string };
    }
  | { errorType: typeof PersonErrorType.INVITATION_ALREADY_RESCINDED; data: { invitationId: string } }
  | { errorType: typeof PersonErrorType.INVITATION_ALREADY_ACCEPTED; data: { personId: string } }
  | {
      errorType: typeof PersonErrorType.PERSON_MISSING_EMAIL_ADDRESS;
      data: { personId?: string; invitationId?: string };
    }
  | { errorType: typeof PersonErrorType.INVITATION_INACTIVE; data: { invitationId: string; status: string } }
  | { errorType: typeof PersonErrorType.LINK_DESTINATION_MISSING; data: { linkType: string } }
  | { errorType: typeof PersonErrorType.LINK_PROJECT_ID_REQUIRED; data: { linkType: string } }
  | { errorType: typeof PersonErrorType.LINK_PROJECT_ID_INVALID; data: { linkType: string; destination: string } }
  | { errorType: typeof PersonErrorType.LINK_AUTH_REQUIRED; data: { linkType: string } }
  | { errorType: typeof PersonErrorType.LINK_NOT_FOUND; data: { linkId: string } }
  | { errorType: typeof PersonErrorType.LINK_TYPE_NOT_SUPPORTED; data: { linkId: string; linkType: string } }
  | { errorType: typeof PersonErrorType.LINK_ACCESS_FAILED; data: { linkId: string } }
  | { errorType: typeof PersonErrorType.INVALID_API_KEY; data: Record<string, never> }
  | { errorType: typeof PersonErrorType.ORGANIZATION_NOT_FOUND; data: { organizationId?: string; personId?: string } }
  | { errorType: typeof PersonErrorType.UNAUTHORIZED; data: { organizationId?: string; personId?: string } }
  | { errorType: typeof PersonErrorType.PERSON_GROUP_NOT_FOUND; data: { personGroupId: string } }
  | { errorType: typeof PersonErrorType.PERSON_GROUP_KEY_ALREADY_EXISTS; data: { key: string } }
  | { errorType: typeof PersonErrorType.PERSON_GROUP_KEY_NOT_FOUND; data: { key: string } }
  | {
      errorType: typeof PersonErrorType.PERSON_GROUP_ARCHIVE_BLOCKED;
      data: { personGroupId: string; key: string };
    }
  | {
      errorType: typeof PersonErrorType.ACCOUNT_MANAGER_PROMOTE_BLOCKED;
      data: {
        personId: string;
        reason:
          | "NOT_INTERNAL"
          | "DISABLED"
          | "MISSING_OPEN_PHONE_LINE"
          | "MULTIPLE_OPEN_PHONE_LINES"
          | "REQUIRES_PERMISSIONS_MANAGER";
      };
    }
  | {
      errorType: typeof PersonErrorType.ACCOUNT_MANAGEMENT_GROUP_REQUIRES_PROMOTE_FLOW;
      data: { groupKey: string };
    }
  | {
      errorType: typeof PersonErrorType.COVERING_PERSON_INELIGIBLE;
      data: {
        personId: string;
        coveringPersonId: string;
        reason: "SELF" | "NOT_FOUND" | "DIFFERENT_ORGANIZATION" | "DISABLED" | "NOT_MENTIONABLE";
      };
    }
  | { errorType: typeof PersonErrorType.UNKNOWN_CONTACT_NOT_FOUND; data: { contactId: string } }
  | {
      errorType: typeof PersonErrorType.UNKNOWN_CONTACT_CLASSIFICATION_FAILED;
      data: {
        operation: "computeFullClassification" | "classifyClustersBatchTooLarge" | "classifyClustersIncompleteResult";
      };
    }
  | {
      errorType: typeof PersonErrorType.UNKNOWN_CONTACT_DRAIN_RUN_ID_INVALID;
      data: { runId: string; pattern: string };
    }
  | {
      errorType: typeof PersonErrorType.DUPLICATE_CLAIMANT_MERGE_RUN_ID_INVALID;
      data: { runId: string; pattern: string };
    }
  | {
      errorType: typeof PersonErrorType.UNKNOWN_CONTACT_MANIFEST_RUN_ID_MISSING;
      data: { key: string };
    }
  | {
      errorType: typeof PersonErrorType.UNKNOWN_CONTACT_CHUNKED_MERGE_IN_TRANSACTION;
      data: { unknownContactId: string };
    }
  | {
      errorType: typeof PersonErrorType.UNKNOWN_CONTACT_CHUNK_BUDGET_EXCEEDED;
      data: { unknownContactId: string; maxChunks: number };
    }
  | {
      errorType: typeof PersonErrorType.UNKNOWN_CONTACT_RETENTION_RUN_ID_INVALID;
      data: { runId: string; pattern: string };
    }
  | {
      errorType: typeof PersonErrorType.UNKNOWN_CONTACT_RETENTION_TIER_INVALID;
      data: { tier: string | null; allowed: string[] };
    }
  | { errorType: typeof PersonErrorType.NAV_KEY_INVALID; data: { navKey: string } }
  | { errorType: typeof PersonErrorType.SIDEBAR_ROW_NOT_REORDERABLE; data: { rowIds: string[] } };

export const personErrorDetails: Record<PersonErrorType, ErrorDetails> = {
  UNKNOWN: { classification: ErrorClassification.UNEXPECTED_ERROR, sentryDescription: "Unknown Error" },
  UNAUTHORIZED: { classification: ErrorClassification.AUTHORIZATION_ERROR, sentryDescription: "Unauthorized" },
  PERSON_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Person not found",
  },
  USER_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "User not found",
  },
  PERSON_NOT_FOUND_FOR_EMAIL_INVITE: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Failed to find newly created user for email invite",
  },
  PERSON_INVITE_CONFLICT: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "User has already accepted an invite",
  },
  PERSON_ID_REQUIRED: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Person ID is required",
  },
  PERSON_NOT_FOUND_BY_PHONE: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Failed to find person by phone number",
  },
  PERSON_MISSING_PRIMARY_PHONE: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Person must have a primary phone number",
  },
  PERSON_PROFILE_PHOTO_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Person profile photo not found",
  },
  INVITATION_ALREADY_RESCINDED: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "Invitation is no longer active",
  },
  INVITATION_ALREADY_ACCEPTED: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "User has already accepted an invite",
  },
  PERSON_MISSING_EMAIL_ADDRESS: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Person does not have an email address",
  },
  INVITATION_INACTIVE: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "Cannot resend inactive invitation",
  },
  LINK_DESTINATION_MISSING: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Missing destination during link creation",
  },
  LINK_PROJECT_ID_REQUIRED: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Project ID required for this link type",
  },
  LINK_PROJECT_ID_INVALID: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Invalid project ID in link destination",
  },
  LINK_AUTH_REQUIRED: {
    classification: ErrorClassification.AUTHORIZATION_ERROR,
    sentryDescription: "Authentication required to access this link type",
  },
  LINK_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Link not found",
  },
  LINK_TYPE_NOT_SUPPORTED: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Link type not supported for this operation",
  },
  LINK_ACCESS_FAILED: {
    classification: ErrorClassification.UNEXPECTED_ERROR,
    sentryDescription: "Failed to access link",
  },
  INVALID_API_KEY: {
    classification: ErrorClassification.AUTHORIZATION_ERROR,
    sentryDescription: "Invalid or expired API key",
  },
  ORGANIZATION_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Organization not found",
  },
  PERSON_GROUP_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Person group not found",
  },
  PERSON_GROUP_KEY_ALREADY_EXISTS: {
    classification: ErrorClassification.CONFLICT_ERROR,
    sentryDescription: "Person group key already in use",
  },
  PERSON_GROUP_KEY_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "No person group found for the given key",
  },
  PERSON_GROUP_ARCHIVE_BLOCKED: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription:
      "This team still has a key that code routes by. Remove its PersonGroupKey from the enum, then archive it.",
  },
  ACCOUNT_MANAGER_PROMOTE_BLOCKED: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription:
      "Account-manager promotion was blocked; see data.reason (org, disabled, phone line, or permissions manager)",
  },
  ACCOUNT_MANAGEMENT_GROUP_REQUIRES_PROMOTE_FLOW: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "Account-management group membership must be set via the promote-to-account-manager flow",
  },
  COVERING_PERSON_INELIGIBLE: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Covering person is not eligible to cover the target person; see data.reason",
  },
  UNKNOWN_CONTACT_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Unknown contact not found for reconciliation",
  },
  UNKNOWN_CONTACT_CLASSIFICATION_FAILED: {
    classification: ErrorClassification.UNEXPECTED_ERROR,
    sentryDescription: "Unknown contact reconciliation classification could not be computed for the batch",
  },
  UNKNOWN_CONTACT_DRAIN_RUN_ID_INVALID: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "DRAIN_RUN_ID contains characters that cannot name a run log or manifest key",
  },
  DUPLICATE_CLAIMANT_MERGE_RUN_ID_INVALID: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "MERGE_RUN_ID contains characters that cannot name a run log or manifest key",
  },
  UNKNOWN_CONTACT_MANIFEST_RUN_ID_MISSING: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "MANIFEST_RUN_ID was not set for an unknown-contact merge undo or purge",
  },
  UNKNOWN_CONTACT_CHUNKED_MERGE_IN_TRANSACTION: {
    classification: ErrorClassification.UNEXPECTED_ERROR,
    sentryDescription:
      "A chunked unknown-contact merge was called inside a transaction, where its per-chunk transactions would join the outer one",
  },
  UNKNOWN_CONTACT_CHUNK_BUDGET_EXCEEDED: {
    classification: ErrorClassification.UNEXPECTED_ERROR,
    sentryDescription: "A chunked unknown-contact merge kept finding rows to move past its chunk budget",
  },
  UNKNOWN_CONTACT_RETENTION_RUN_ID_INVALID: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "RETENTION_RUN_ID contains characters that cannot name a manifest key",
  },
  UNKNOWN_CONTACT_RETENTION_TIER_INVALID: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "TIER was missing or is not one of the zero-claimant retention tiers",
  },
  NAV_KEY_INVALID: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Nav key is not a favoritable page or view",
  },
  SIDEBAR_ROW_NOT_REORDERABLE: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Sidebar reorder named a row that holds no position",
  },
};
